// The string values are what the Auth service puts in the JWT `authorities`
// and `roles` claims. They are a backend contract: renaming one silently
// removes an access check.

export const Authority = {
  User: "LOGIN",

  // survey
  ViewSurvey: "VIEW_SURVEY",
  CreateSurvey: "CREATE_SURVEY",
  EditSurvey: "EDIT_SURVEY",
  PublishSurvey: "PUBLISH_SURVEY",
  DownloadSurvey: "DOWNLOAD_SURVEY_RESPONSE",
  DeleteSurvey: "DELETE_SURVEY",

  // newsfeed
  ViewNewsfeed: "VIEW_NEWSFEED",
  CreateNewsfeed: "CREATE_NEWSFEED",
  EditNewsfeed: "EDIT_NEWSFEED",
  DeleteNewsfeed: "DELETE_NEWSFEED",

  // clergy
  ViewClergy: "VIEW_CLERGY",

  // parish
  ViewParish: "VIEW_PARISH",

  // users
  ViewUsers: "VIEW_USER",
  CreateUser: "CREATE_USER",
  EditUser: "EDIT_USER",
  DeleteUser: "DELETE_USER",
  ApproveUser: "REVIEW_USER",
  DisableUser: "DISABLE_USER",

  // templates
  ViewTemplatesList: "VIEW_TEMPLATES_LIST",
  EditTemplate: "EDIT_TEMPLATE",
  AddTemplate: "ADD_TEMPLATE",
  ViewTemplate: "VIEW_TEMPLATE",
  DeleteTemplate: "DELETE_TEMPLATE",
  EditTemplateType: "EDIT_TEMPLATE_TYPE",

  // roles
  ViewRoles: "VIEW_ROLE",
  CreateRole: "CREATE_ROLE",
  DeleteRole: "DELETE_ROLE",
  EditRole: "EDIT_ROLE",

  // registration
  ViewRegistrationProperty: "VIEW_REGISTRATION_PROPERTY",
  EditRegistrationProperty: "EDIT_REGISTRATION_PROPERTY",

  // notifications
  ViewNotifications: "VIEW_NOTIFICATIONS",
  AddNotification: "ADD_NOTIFICATION",
  DeleteNotification: "DELETE_NOTIFICATION",
  EditNotification: "EDIT_NOTIFICATION",

  // calendar
  ViewCalendar: "VIEW_CALENDAR",
  ApproveCalendar: "APPROVE_CALENDAR",
} as const;

export type Authority = (typeof Authority)[keyof typeof Authority];

export const Role = {
  USER: "APP_USER",
  ADMIN: "ADMIN_USER",
  SITE_ADMIN: "SUPER_ADMIN",
} as const;

export type Role = (typeof Role)[keyof typeof Role];

/** The claims the app reads out of the access token. */
export interface Principal {
  authorities: string[];
  roles: string[];
  /** `user_name` in the token -- it holds the user id, not a name. */
  userId: string;
  email?: string;
  exp?: number;
}

/**
 * Whether the UI should offer something -- a sidebar link, an action button.
 * SUPER_ADMIN passes regardless of the authority list on the token.
 */
export function canSee(principal: Principal | null, authority: Authority): boolean {
  if (!principal) return false;
  return principal.authorities.includes(authority) || principal.roles.includes(Role.SITE_ADMIN);
}

/** `canSee` for a set -- true if any one of them passes. */
export function canSeeAny(principal: Principal | null, authorities: readonly Authority[]): boolean {
  return authorities.some((a) => canSee(principal, a));
}

/**
 * Whether the user may enter a route: their authorities intersect the route's,
 * or they are SUPER_ADMIN. Must stay consistent with `canSee`, so the sidebar
 * never offers a link the guard then refuses.
 */
export function canEnterRoute(
  principal: Principal | null,
  authorities: readonly Authority[],
): boolean {
  if (!principal) return false;
  if (principal.roles.includes(Role.SITE_ADMIN)) return true;
  return principal.authorities.some((a) => (authorities as readonly string[]).includes(a));
}
